import * as Apollo from "@apollo/client";
import { Icon as DsIcon } from "@equipmentshare/ds-icon";
import * as Ant from "antd";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import { Graph, gql } from "~/Graph";
import { Toast } from "~/Toast";
import * as Typography from "~/Typography";

export type GraphError = {
  readonly message: string;
  readonly __typename?: string;
};

export type Errors = {
  readonly graphError: GraphError;
};
export namespace Errors {
  export type GraphErrors = { readonly graphErrors?: readonly GraphError[] };
  export type Fragment = Graph.Generated.ErrorFragment;
  export namespace Fragment {
    export const document = () => gql`
      fragment Error on Error {
        message
      }
    `;

    export const decode = (original: Fragment): Errors => ({
      graphError: original,
    });
  }

  export function isGraphError<T>(graphError?: Errors | T) {
    // @ts-ignore
    return graphError && "graphError" in graphError;
  }

  export type ClientErrors = {
    readonly errors?: Apollo.ApolloError;
    readonly warnings?: Errors;
  };

  export function onError<T>(
    apolloError?: Apollo.ApolloError | Error,
    graphError?: Errors | T
  ): ClientErrors | undefined {
    if (apolloError instanceof Apollo.ApolloError) {
      console.log("apolloError:", apolloError);
      return {
        errors: {
          graphQLErrors: apolloError.graphQLErrors,
          networkError: apolloError.networkError,
          clientErrors: apolloError.clientErrors,
          extraInfo: apolloError.extraInfo,
          name: apolloError?.name,
          message: apolloError?.message,
        },
      };
    // @ts-ignore
    } else if (graphError && "graphError" in graphError) {
      if (
        graphError.graphError?.__typename === "Error__AccessForbidden" ||
        graphError.graphError?.__typename === "Error__DatabaseBadRequest" ||
        graphError.graphError?.__typename === "Error__DatabaseException" ||
        graphError.graphError?.__typename === "Error__DatabaseEngine" ||
        graphError.graphError?.__typename === "Error__UnclassifiedError" ||
        graphError.graphError?.__typename === "Error__UnexpectedRESTResponse"
      ) {
        return {
          errors: {
            graphQLErrors: [],
            networkError: null,
            clientErrors: [],
            extraInfo: null,
            name: graphError.graphError?.__typename,
            message: graphError.graphError?.message,
          },
        };
      }
      return {
        warnings: graphError,
      };
    }
    return undefined;
  }

  export const renderApolloError = (
    title: string,
    error: Apollo.ApolloError | Error
  ) => {
    if (error instanceof Apollo.ApolloError || "graphQLErrors" in error) {
      // @ts-ignore
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        return (
          <ErrorContainer>
            {
              // @ts-ignore
              void error.graphQLErrors.forEach((error) => (
                <Toast message={title} description={error.message} />
              ))
            }
          </ErrorContainer>
        );
      }
      // @ts-ignore
      if (error.clientErrors && error.clientErrors.length > 0) {
        return (
          <ErrorContainer>
            {
              // @ts-ignore
              void error.clientErrors.forEach((error) => (
                <Toast message={title} description={error.message} />
              ))
            }
          </ErrorContainer>
        );
      }
      // @ts-ignore
      if (error.networkError) {
        return (
          // @ts-ignore
          <Toast message={title} description={error.networkError.message} />
        );
      }
    }
    return <Toast message={title} description={error.message} />;
  };

  export const displayMessage = (graphError: Graph.Errors): void => {
    void Ant.message.error({
      icon: (
        <DsIcon
          name="$icon_alert_fill"
          color="$color_primary_red"
          layout={{ margin: { top: "$spacing_2", right: "$spacing_3" } }}
        />
      ),
      content: (
        <Typography.Component color={Color.guardsmanRed()} align="center">
          {graphError.graphError.message}
        </Typography.Component>
      ),
      duration: 10,
    });
  };

  export const ErrorContainer = styled.div`
    display: flex;
  `;
}
